import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { ParentChildOutputWhitelistInfo } from '@app/features/scope-overview/components/change-scope-output-modal/change-scope-output-modal.component'
import { CompanyGroup } from '@app/features/scope-overview/model/company-group.model'
import { CostPlus } from '@app/features/scope-overview/model/cost-plus.model'
import { OutputTemplate } from '@app/features/scope-overview/model/output-template.model'
import { RatecardStructureVersion } from '@app/features/scope-overview/model/ratecard-structure.model'
import { RatecardVersion } from '@app/features/scope-overview/model/ratecard-version.model'
import { ScopeType } from '@app/features/scope-overview/model/scope-type.model'
import { SpreadsheetTemplate } from '@app/features/scope-overview/model/spreadsheet-template.model'
import { Page } from '@app/features/scoping/models/page.model'
import { SecondParty } from '@core/model/second-party.model'
import { environment } from '@envs/environment'
import { plainToClassFromExist, plainToInstance } from 'class-transformer'
import { map, Observable } from 'rxjs'
import { Deliverable, LibraryTaskTemplate } from '@app/features/scoping/models/deliverable.model'
import { NewTask } from '@core/model/new-task.model'
import { Sort } from '@angular/material/sort'
import { ComponentModifier } from '@app/features/company-management/models/component-modifier.model'
import { ComponentEntryFilter } from '@core/model/component-entry-filter.model'
import { Pagination } from '@core/model/definitions/pagination.interface'
import { ComponentEntryDetailsView } from '@app/features/company-management/models/component-entry-details-view'
import { ExchangeRateFilter } from '@core/model/exchange-rate-filter.model'
import { ExchangeRate } from '@app/features/scope-overview/model/exchange-rate.model'
import { ScopeDynamicFieldSettingModel } from '@app/features/scoping/models/scope-dynamic-field-setting.model'
import { ComponentEntryComplexityRole } from '@app/features/company-management/models/component-entry-complexity-role.model'

@Injectable({ providedIn: 'root' })
export class CompanyManagementService {
  private readonly BASE_API_URL: string = environment.scopeApiUrl

  public currencies = [
    'GBP',
    'EUR',
    'AUD',
    'USD',
    'CAD',
    'SGD',
    'CZK',
    'SEK',
    'DKK',
    'PLN',
    'NOK',
    'ZAR',
    'AED',
    'CHF',
    'JPY',
    'THB',
    'KES',
    'MYR',
    'INR',
    'CNY',
    'HKD',
    'BRL',
    'LVL',
    'LTL',
    'HUF',
    'VND',
    'ISK',
    'TWD',
    'MXN',
    'MAD',
    'NZD',
    'SAR',
    'QAR',
    'IDR',
    'KHR',
    'ILS',
    'MDL',
    'NGN',
    'ARS',
    'COP',
    'CLP',
    'UGX',
    'MUR',
    'RON',
    'PHP',
  ]

  constructor(private http: HttpClient) {}

  getParentOutputTemplatesWhitelist(companyId: number) {
    return this.http
      .get<ParentChildOutputWhitelistInfo>(`${this.BASE_API_URL}company/child-companies/${companyId}/whitelist/output`)
      .pipe(map((api) => plainToInstance(ParentChildOutputWhitelistInfo, api)))
  }

  getAllWordTemplates() {
    return this.http
      .get<OutputTemplate[]>(`${this.BASE_API_URL}company/custom-output-template?includeFields=false`)
      .pipe(map((api) => plainToInstance(OutputTemplate, api)))
  }

  getScopeWordTemplates() {
    return this.http
      .get<OutputTemplate[]>(`${this.BASE_API_URL}company/custom-output-template/scope?includeFields=false`)
      .pipe(map((api) => plainToInstance(OutputTemplate, api)))
  }

  getAllXlsxTemplates() {
    return this.http
      .get<SpreadsheetTemplate[]>(`${this.BASE_API_URL}xlsx-templates`)
      .pipe(map((api) => plainToInstance(SpreadsheetTemplate, api)))
  }

  getWordTemplate(templateId: number) {
    return this.http
      .get<OutputTemplate>(`${this.BASE_API_URL}company/custom-output-template/${templateId}`)
      .pipe(map((api) => plainToInstance(OutputTemplate, api)))
  }

  retrieveCompanyDynamicFields() {
    return this.http
      .get<ScopeDynamicFieldSettingModel[]>(`${this.BASE_API_URL}scope/dynamic-field-setting`)
      .pipe(map((api) => plainToInstance(ScopeDynamicFieldSettingModel, api)))
  }

  getXlsxTemplate(templateId: number) {
    return this.http
      .get<SpreadsheetTemplate>(`${this.BASE_API_URL}xlsx-templates/${templateId}`)
      .pipe(map((api) => plainToInstance(SpreadsheetTemplate, api)))
  }

  updateCompanyCustomOutputTemplate(template: OutputTemplate) {
    return this.http
      .put(`${this.BASE_API_URL}company/custom-output-template-manage`, template)
      .pipe(map((api) => plainToInstance(OutputTemplate, api)))
  }

  updateXlsxOutputTemplate(template: SpreadsheetTemplate) {
    return this.http.put(`${this.BASE_API_URL}xlsx-templates`, template)
  }

  deleteXlsxOutputTemplate(templateId: number) {
    return this.http.delete(`${this.BASE_API_URL}xlsx-templates/${templateId}`)
  }

  downloadExcelTemplate(templateId: number) {
    return this.http.get(`${this.BASE_API_URL}xlsx-templates/download/${templateId}`, {
      responseType: 'blob',
      observe: 'response',
    })
  }

  uploadExcelTemplate(file: File, templateId: number) {
    const formData = new FormData()
    formData.append('image', file)
    return this.http
      .post(`${this.BASE_API_URL}xlsx-templates/upload/${templateId}`, formData)
      .pipe(map((api) => plainToInstance(SpreadsheetTemplate, api)))
  }

  deleteCompanyCustomOutputTemplate(templateId: number) {
    return this.http.delete(`${this.BASE_API_URL}company/custom-output-template/${templateId}`)
  }

  exportScopeOutputToDocx(templateId: number, downloadFileName?: string) {
    return this.http.get(
      `${this.BASE_API_URL}scope/${templateId}/export/download${downloadFileName ? '?downloadFileName=' + downloadFileName : ''}`,
      { responseType: 'blob', observe: 'response' }
    )
  }

  exportFolderOutputToDocx(templateId: number) {
    return this.http.get(`${this.BASE_API_URL}sow/${templateId}/export/preview`, {
      responseType: 'blob',
      observe: 'response',
    })
  }

  duplicateCompanyCustomOutputTemplate(templateId: number, newName: string) {
    return this.http.post(`${this.BASE_API_URL}company/custom-output-template/${templateId}`, { newName })
  }

  uploadCustomTemplateDocxBaseTemplate(templateId: number, file: File) {
    const formData = new FormData()
    formData.append('image', file)
    return this.http
      .post(`${this.BASE_API_URL}company/custom-output-template/${templateId}/base-docx-template`, formData)
      .pipe(map((api) => plainToInstance(OutputTemplate, api)))
  }

  removeCustomTemplateDocxBaseTemplate(templateId: number) {
    return this.http
      .delete(`${this.BASE_API_URL}company/custom-output-template/${templateId}/base-docx-template`)
      .pipe(map((api) => plainToInstance(OutputTemplate, api)))
  }

  previewExport(templateId: number) {
    return this.http.get(`${this.BASE_API_URL}scope/${templateId}/export/preview`, { observe: 'response' }).pipe(
      map((response) => {
        const contentDisposition = response.headers.get('content-disposition')
        const name = contentDisposition.split('name="')[1].split('"')[0]
        const filename = contentDisposition.split('filename="')[1].split('"')[0]
        return { data: response.body, name: name, filename: filename }
      })
    )
  }

  createCompanyCustomOutputTemplate(template) {
    return this.http
      .post(`${this.BASE_API_URL}company/custom-output-template`, template)
      .pipe(map((api) => plainToInstance(OutputTemplate, api)))
  }

  createXlsxOutputTemplate(template) {
    return this.http
      .post(`${this.BASE_API_URL}xlsx-templates`, template)
      .pipe(map((api) => plainToInstance(SpreadsheetTemplate, api)))
  }

  getOwnSecondPartiesAndGroupShared(pageNumber: number, pageSize: number, searchText: string) {
    let url = `${this.BASE_API_URL}second-party/page?pageNumber=${pageNumber}&pageSize=${pageSize}`
    if (searchText && searchText.length) {
      url = url + `&searchText=${searchText}`
    }
    return this.http
      .get<Page<SecondParty>>(url)
      .pipe(map((api) => plainToClassFromExist(new Page<SecondParty>(SecondParty), api)))
  }

  addSecondParty(secondParty) {
    return this.http
      .post(`${this.BASE_API_URL}second-party`, secondParty)
      .pipe(map((api) => plainToInstance(SecondParty, api)))
  }

  getAllRateCards(includeArchived = false, includeUnarchived = true) {
    return this.http
      .get<
        RatecardVersion[]
      >(`${this.BASE_API_URL}company/ratecard?includeArchived=${includeArchived}&includeUnarchived=${includeUnarchived}`)
      .pipe(map((api) => plainToInstance(RatecardVersion, api)))
  }

  deleteSecondParty(id: number) {
    return this.http.delete(`${this.BASE_API_URL}second-party/${id}`)
  }

  deleteRateCard(id: number) {
    return this.http.delete(`${this.BASE_API_URL}company/ratecard/${id}`)
  }

  unarchiveRateCard(id: number) {
    return this.http
      .put(`${this.BASE_API_URL}company/ratecard/${id}/unarchive`, {})
      .pipe(map((api) => plainToInstance(RatecardVersion, api)))
  }

  getRateCardStructures(): Observable<RatecardStructureVersion[]> {
    return this.http
      .get(`${this.BASE_API_URL}ratecard-structure`)
      .pipe(
        map((api: { content: RatecardStructureVersion[] }) => plainToInstance(RatecardStructureVersion, api.content))
      )
  }

  getAllCostPluses() {
    return this.http
      .get<CostPlus[]>(`${this.BASE_API_URL}company/cost-plus`)
      .pipe(map((api) => plainToInstance(CostPlus, api)))
  }

  createRatecardFromStructure(rateCard) {
    return this.http
      .post(`${this.BASE_API_URL}company/ratecard/from-structure`, rateCard)
      .pipe(map((api) => plainToInstance(RatecardVersion, api)))
  }

  createCompanyGroup(group) {
    return this.http
      .post(`${this.BASE_API_URL}company/group`, group)
      .pipe(map((api) => plainToInstance(CompanyGroup, api)))
  }

  getCompanyGroups() {
    return this.http
      .get<CompanyGroup[]>(`${this.BASE_API_URL}company/group`)
      .pipe(map((api) => plainToInstance(CompanyGroup, api)))
  }

  getScopeTypes() {
    return this.http
      .get<CompanyGroup[]>(`${this.BASE_API_URL}scope-type`)
      .pipe(map((api) => plainToInstance(ScopeType, api)))
  }

  createScopeType(scopeType) {
    return this.http
      .post(`${this.BASE_API_URL}scope-type`, scopeType)
      .pipe(map((api) => plainToInstance(ScopeType, api)))
  }

  updateScopeType(scopeType) {
    return this.http
      .put(`${this.BASE_API_URL}scope-type/${scopeType.id}`, scopeType)
      .pipe(map((api) => plainToInstance(ScopeType, api)))
  }

  deleteScopeType(id: number) {
    return this.http.delete(`${this.BASE_API_URL}scope-type/${id}`)
  }

  getRateCardMetaStructure() {
    return this.http.get(`${this.BASE_API_URL}company/ratecard/meta-structure`)
  }

  getRateCard(rateCardId: number, versionNumber?: number) {
    const url = `${this.BASE_API_URL}company/ratecard/${rateCardId}`
    return this.http
      .get(url, {
        params: versionNumber !== undefined && versionNumber !== null ? { version: versionNumber.toString() } : {},
      })
      .pipe(map((api) => plainToInstance(RatecardVersion, api)))
  }

  verify(file, type) {
    const formData = new FormData()
    formData.append('file', file)
    return this.http.post(`${this.BASE_API_URL}company/import/verify/${type}`, formData, {
      responseType: 'arraybuffer',
    })
  }

  updateSecondPartyRateCards(secondParty: SecondParty) {
    return this.http.put(
      `${this.BASE_API_URL}second-party/${secondParty.id}/ratecards/`,
      secondParty.rateCardIdentities
    )
  }

  import(file, type) {
    const formData = new FormData()
    formData.append('image', file)
    return this.http.post(`${this.BASE_API_URL}company/import/${type}`, formData, {
      responseType: 'arraybuffer',
    })
  }

  syncTrafficImportsSecondParty(syncName: boolean) {
    return this.syncTrafficImports({ secondpartySync: true, syncName: syncName })
  }

  syncTrafficImportsRateCard(syncName: boolean) {
    return this.syncTrafficImports({ ratecardSync: true, syncName: syncName })
  }

  syncTrafficImports(config) {
    let url = `${this.BASE_API_URL}company/traffic-system/force-sync?`
    if (config.ratecardSync) {
      url += 'ratecardSync=true&'
    }

    if (config.secondpartySync) {
      url += 'secondpartySync=true&'
    }

    if (config.syncName) {
      url += 'syncName=true'
    }

    return this.http.post(url, null)
  }

  renderTrafficSystemErrorOnSync(type) {
    return this.http.get(`${this.BASE_API_URL}company/traffic-system/force-sync/last-imported-data/${type}`)
  }

  getTrafficSystemSecondParties(pageNumber) {
    return this.http.get(`${this.BASE_API_URL}company/traffic-system/second-parties?pageNumber=${pageNumber}`)
  }

  getTrafficSystemRateCards(pageNumber) {
    return this.http.get(`${this.BASE_API_URL}company/traffic-system/ratecard?pageNumber=${pageNumber}`)
  }

  renderTrafficSystemSecondPartiesError(pageNumber) {
    return this.http.get(
      `${this.BASE_API_URL}company/traffic-system/second-parties/last-imported-data/page-number/${pageNumber}`
    )
  }

  renderTrafficSystemRateCardError(pageNumber) {
    return this.http.get(
      `${this.BASE_API_URL}company/traffic-system/ratecard/last-imported-data/page-number/${pageNumber}`
    )
  }

  importTrafficSystemSecondParties(selectedIdsToNewNameJson) {
    return this.http
      .post<SecondParty[]>(`${this.BASE_API_URL}company/traffic-system/second-parties/import`, selectedIdsToNewNameJson)
      .pipe(map((api) => plainToInstance(SecondParty, api)))
  }

  importTrafficSystemRateCards(selectedIdsToNewNameJson) {
    return this.http
      .post<
        RatecardVersion[]
      >(`${this.BASE_API_URL}company/traffic-system/ratecard/import`, { selectedRatecardIdsToName: selectedIdsToNewNameJson })
      .pipe(map((api) => plainToInstance(RatecardVersion, api)))
  }

  duplicateRateCard(ratecardId, opt) {
    return this.http
      .post<RatecardVersion>(`${this.BASE_API_URL}company/ratecard/${ratecardId}/duplicate`, opt)
      .pipe(map((api) => plainToInstance(RatecardVersion, api)))
  }

  getAllPreviousRolesChain(roleId: number, depth = 1): Observable<any[]> {
    return this.http.get<any[]>(`${this.BASE_API_URL}company/ratecard/role/${roleId}/previous-chain`, {
      params: { depth: depth.toString() },
    })
  }

  getTrafficSystemRatecardsByExternalId(externalIds) {
    return this.http.get(
      `${this.BASE_API_URL}company/traffic-system/ratecard/${externalIds instanceof Array ? externalIds.join(',') : externalIds}`
    )
  }

  requestExportOfRatecard(rateCard) {
    const rateCardIdentityId = rateCard?.rateCardIdentity?.id || ''
    const rateCardVersion = rateCard?.version || ''
    return this.http.post(
      `${this.BASE_API_URL}company/ratecard/export?rateCardIdentityId=${rateCardIdentityId}&version=${rateCardVersion}`,
      null
    )
  }

  exportTasks(rateCardIdentityId?: number) {
    return this.http.post(
      `${this.BASE_API_URL}library/task-template/export?rateCardIdentityId=${rateCardIdentityId || ''}`,
      null
    )
  }

  getLibraryTaskTemplates(pageNumber: number, searchText: string, sort: Sort) {
    let url = `${this.BASE_API_URL}library/task-template/page?pageNumber=${pageNumber}&orderByField=${sort.active}&order=${sort.direction.toUpperCase()}`
    if (searchText && searchText.length) {
      url = url + `&searchText=${searchText}`
    }
    return this.http
      .get<Page<Deliverable>>(url)
      .pipe(map((api) => plainToClassFromExist(new Page<Deliverable>(Deliverable), api)))
  }

  addLibraryTaskTemplate(task: NewTask): Observable<LibraryTaskTemplate> {
    return this.http.post<any>(`${this.BASE_API_URL}library/task-template`, task)
  }

  deleteLibraryTaskTemplate(taskId: number) {
    return this.http.delete(`${this.BASE_API_URL}library/task-template/${taskId}`)
  }

  getLibraryTaskTemplate(taskId: number, ratecardVersionId: number) {
    return this.http
      .get(`${this.BASE_API_URL}library/task-template/${taskId}/ratecard/${ratecardVersionId}`)
      .pipe(map((api) => plainToInstance(LibraryTaskTemplate, api)))
  }

  updateLibraryTaskTemplate(task: LibraryTaskTemplate) {
    return this.http
      .put<any>(`${this.BASE_API_URL}library/task-template/${task.id}`, task)
      .pipe(map((api) => plainToInstance(LibraryTaskTemplate, api)))
  }

  getCompanyRateCards() {
    return this.http
      .get<RatecardVersion[]>(`${this.BASE_API_URL}company/ratecards`)
      .pipe(map((api) => plainToInstance(RatecardVersion, api)))
  }

  getCompanyRateCardsPaged(page: number, pageSize: number, searchText?: string) {
    let url = `${this.BASE_API_URL}company/ratecards/page?page=${page}&pageSize=${pageSize}`
    if (searchText && searchText.length) {
      url = url + `&searchText=${searchText}`
    }
    return this.http
      .get<Page<RatecardVersion>>(url)
      .pipe(map((api) => plainToClassFromExist(new Page<RatecardVersion>(RatecardVersion), api)))
  }

  cascadeRatecardChangesToLibraryItemsAndTasks(sourceRateCard, targetRateCard, mapping) {
    return this.http.post(
      `${this.BASE_API_URL}library/item/entries/ratecard/${sourceRateCard.id}/duplicate?targetRateCardId=${targetRateCard.id}`,
      mapping
    )
  }

  getComponentModifiers(scenarioId: number) {
    const url = `${this.BASE_API_URL}scenarios/${scenarioId}/component-formulas`
    return this.http.get<ComponentModifier[]>(url).pipe(map((api) => plainToInstance(ComponentModifier, api)))
  }

  getComponentModifiersComplexities(scenarioId: number) {
    const url = `${this.BASE_API_URL}scenarios/${scenarioId}/component-formulas/complexities`
    return this.http.get<ComponentEntryComplexityRole[]>(url)
  }

  getComponentModifier(scenarioId: number, modifierId: number) {
    const url = `${this.BASE_API_URL}scenarios/${scenarioId}/component-formulas/${modifierId}`
    return this.http.get<ComponentModifier>(url).pipe(map((api) => plainToInstance(ComponentModifier, api)))
  }

  deleteComponentModifier(scenarioId: number, componentModifierId: number) {
    return this.http.delete(`${this.BASE_API_URL}scenarios/${scenarioId}/component-formulas/${componentModifierId}`)
  }

  createComponentModifier(scenarioId: number, componentModifier: any) {
    const url = `${this.BASE_API_URL}scenarios/${scenarioId}/component-formulas`
    return this.http
      .post<ComponentModifier>(url, componentModifier)
      .pipe(map((api) => plainToInstance(ComponentModifier, api)))
  }

  updateComponentModifier(scenarioId: number, componentModifier: any) {
    const url = `${this.BASE_API_URL}scenarios/${scenarioId}/component-formulas/${componentModifier.id}`
    return this.http.put(url, componentModifier)
  }

  searchComponentEntries(filter: ComponentEntryFilter, pageInfo: Pagination) {
    const httpParams = new HttpParams().set('page', pageInfo.page).set('pageSize', pageInfo.pageSize)

    return this.http.post<Page<ComponentEntryDetailsView>>(
      `${this.BASE_API_URL}library/component-entry-details-view`,
      filter,
      { params: httpParams }
    )
  }

  getComplexityRolesForBulkSelect(filter: ComponentEntryFilter) {
    return this.http.post<number[]>(`${this.BASE_API_URL}library/complexity-roles-for-bulk-select`, filter)
  }

  assignComponentModifier(scenarioId: number, componentModifierId: number, complexities: number[]) {
    if (!complexities?.length)
      return new Observable<void>((obs) => {
        obs.next()
        obs.complete()
      })
    const url = `${this.BASE_API_URL}scenarios/${scenarioId}/component-formulas/${componentModifierId}/component-entry-complexity-roles`
    return this.http.post(url, { complexityIds: complexities })
  }

  unassignComponentModifier(scenarioId: number, componentModifierId: number, complexities: number[]) {
    if (!complexities?.length)
      return new Observable<void>((obs) => {
        obs.next()
        obs.complete()
      })
    const url = `${this.BASE_API_URL}scenarios/${scenarioId}/component-formulas/${componentModifierId}/component-entry-complexity-roles`
    return this.http.delete(url, { body: { complexityIds: complexities } })
  }

  getDisciplines() {
    return this.http.get<any>(`${this.BASE_API_URL}predict/disciplines`)
  }

  getExchangeRates(filter: ExchangeRateFilter, pageInfo: Pagination) {
    const httpParams = new HttpParams().set('page', pageInfo.page).set('pageSize', pageInfo.pageSize)

    return this.http.post<Page<ExchangeRate>>(`${this.BASE_API_URL}company/exchange-rates`, filter, {
      params: httpParams,
    })
  }

  addExchangeRate(exchangeRate: any) {
    return this.http.post<any>(`${this.BASE_API_URL}company/exchange-rate`, exchangeRate)
  }

  editExchangeRate(exchangeRate: any) {
    return this.http.put<any>(`${this.BASE_API_URL}company/exchange-rate/${exchangeRate.id}`, exchangeRate)
  }

  deleteExchangeRate(exchangeRateId: number) {
    return this.http.delete(`${this.BASE_API_URL}company/exchange-rate/${exchangeRateId}`)
  }
}
